import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Table, Form, Button, Badge, Alert, Spinner } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { Search, Filter, ChevronLeft, ChevronRight } from 'lucide-react';
import api from '../services/api';

const PaymentNotificationsPage = () => {
    const { t } = useTranslation();
    const [notifications, setNotifications] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Pagination
    const [pagination, setPagination] = useState({
        total: 0,
        limit: 20,
        offset: 0,
        hasMore: false
    });

    // Filters
    const [filters, setFilters] = useState({
        start_date: '',
        end_date: '',
        source_app: ''
    });

    // Fetch notifications
    const fetchNotifications = async (offset = 0) => {
        try {
            setLoading(true);
            setError(null);

            // Build query params
            const params = new URLSearchParams({
                limit: pagination.limit,
                offset: offset
            });

            if (filters.start_date) params.append('start_date', filters.start_date);
            if (filters.end_date) params.append('end_date', filters.end_date);
            if (filters.source_app) params.append('source_app', filters.source_app);

            const response = await api.get(`/webhooks/payment-notifications?${params.toString()}`);

            if (response.data.success) {
                setNotifications(response.data.data);
                setPagination({
                    ...response.data.pagination,
                    offset: offset
                });
            }
        } catch (err) {
            console.error('Error fetching payment notifications:', err);
            setError(t('paymentNotificationsPage.error_fetching_notifications'));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchNotifications();
        // eslint-disable-next-line
    }, []);

    const handleFilterChange = (e) => {
        setFilters({
            ...filters,
            [e.target.name]: e.target.value
        });
    };

    const handleApplyFilters = () => {
        fetchNotifications(0);
    };

    const handleClearFilters = () => {
        setFilters({
            start_date: '',
            end_date: '',
            source_app: ''
        });
        setTimeout(() => fetchNotifications(0), 100);
    };

    const handlePreviousPage = () => {
        const newOffset = Math.max(0, pagination.offset - pagination.limit);
        fetchNotifications(newOffset);
    };

    const handleNextPage = () => {
        const newOffset = pagination.offset + pagination.limit;
        fetchNotifications(newOffset);
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('es-CO', {
            style: 'currency',
            currency: 'COP',
            minimumFractionDigits: 0
        }).format(amount);
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleString('es-CO', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const getBankBadgeColor = (sourceApp) => {
        const colors = {
            'Nequi': 'danger',
            'Bancolombia': 'warning',
            'DaviPlata': 'success',
            'BBVA': 'primary'
        };
        return colors[sourceApp] || 'secondary';
    };

    return (
        <Container className="mt-4">
            <Row className="mb-4">
                <Col>
                    <h2>📱 {t('paymentNotificationsPage.title')}</h2>
                    <p className="text-muted">
                        {t('paymentNotificationsPage.description')}
                    </p>
                </Col>
            </Row>

            {/* Filters */}
            <Card className="mb-4">
                <Card.Body>
                    <Row className="align-items-end">
                        <Col md={3}>
                            <Form.Group>
                                <Form.Label>{t('paymentNotificationsPage.start_date')}</Form.Label>
                                <Form.Control
                                    type="date"
                                    name="start_date"
                                    value={filters.start_date}
                                    onChange={handleFilterChange}
                                />
                            </Form.Group>
                        </Col>
                        <Col md={3}>
                            <Form.Group>
                                <Form.Label>{t('paymentNotificationsPage.end_date')}</Form.Label>
                                <Form.Control
                                    type="date"
                                    name="end_date"
                                    value={filters.end_date}
                                    onChange={handleFilterChange}
                                />
                            </Form.Group>
                        </Col>
                        <Col md={3}>
                            <Form.Group>
                                <Form.Label>{t('paymentNotificationsPage.bank')}</Form.Label>
                                <Form.Select
                                    name="source_app"
                                    value={filters.source_app}
                                    onChange={handleFilterChange}
                                >
                                    <option value="">{t('paymentNotificationsPage.all')}</option>
                                    <option value="Nequi">Nequi</option>
                                    <option value="Bancolombia">Bancolombia</option>
                                    <option value="DaviPlata">DaviPlata</option>
                                    <option value="BBVA">BBVA</option>
                                </Form.Select>
                            </Form.Group>
                        </Col>
                        <Col md={3}>
                            <div className="d-flex gap-2">
                                <Button variant="primary" onClick={handleApplyFilters}>
                                    <Filter size={16} className="me-2" />
                                    {t('paymentNotificationsPage.filter')}
                                </Button>
                                <Button variant="outline-secondary" onClick={handleClearFilters}>
                                    {t('paymentNotificationsPage.clear')}
                                </Button>
                            </div>
                        </Col>
                    </Row>
                </Card.Body>
            </Card>

            {/* Summary */}
            {!loading && (
                <Alert variant="info" className="mb-4">
                    {t('paymentNotificationsPage.showing_results')} {notifications.length} {t('paymentNotificationsPage.of')} {pagination.total} {t('paymentNotificationsPage.notifications')}
                </Alert>
            )}

            {/* Error */}
            {error && (
                <Alert variant="danger" dismissible onClose={() => setError(null)}>
                    {error}
                </Alert>
            )}

            {/* Loading */}
            {loading ? (
                <div className="text-center py-5">
                    <Spinner animation="border" variant="primary" />
                    <p className="mt-3 text-muted">{t('paymentNotificationsPage.loading')}</p>
                </div>
            ) : (
                <>
                    {/* Table */}
                    <div className="modern-table-wrapper">
                        <Table hover responsive className="modern-table">
                            <thead>
                                <tr>
                                    <th>ID</th>
                                    <th>{t('paymentNotificationsPage.bank')}</th>
                                    <th>{t('paymentNotificationsPage.amount')}</th>
                                    <th>{t('paymentNotificationsPage.sender')}</th>
                                    <th>{t('paymentNotificationsPage.received_at')}</th>
                                    <th>{t('paymentNotificationsPage.notification_text')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {notifications.length === 0 ? (
                                    <tr>
                                        <td colSpan="6" className="text-center py-5 text-muted">
                                            <Search size={48} className="mb-3 opacity-50" />
                                            <p>{t('paymentNotificationsPage.no_notifications')}</p>
                                        </td>
                                    </tr>
                                ) : (
                                    notifications.map((notification) => (
                                        <tr key={notification.id}>
                                            <td>
                                                <span className="text-muted">#{notification.id}</span>
                                            </td>
                                            <td>
                                                <Badge bg={getBankBadgeColor(notification.source_app)}>
                                                    {notification.source_app}
                                                </Badge>
                                            </td>
                                            <td>
                                                <strong className="text-success">
                                                    {formatCurrency(notification.amount)}
                                                </strong>
                                            </td>
                                            <td>
                                                {notification.sender_raw || <span className="text-muted">-</span>}
                                            </td>
                                            <td>
                                                <small>{formatDate(notification.received_at)}</small>
                                            </td>
                                            <td>
                                                <small className="text-muted text-truncate d-inline-block" style={{ maxWidth: '300px' }}>
                                                    {notification.raw_notification_text}
                                                </small>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </Table>
                    </div>

                    {/* Pagination */}
                    {pagination.total > pagination.limit && (
                        <Row className="mt-4">
                            <Col className="d-flex justify-content-between align-items-center">
                                <Button
                                    variant="outline-primary"
                                    onClick={handlePreviousPage}
                                    disabled={pagination.offset === 0}
                                >
                                    <ChevronLeft size={16} className="me-1" />
                                    {t('paymentNotificationsPage.previous')}
                                </Button>

                                <span className="text-muted">
                                    {t('paymentNotificationsPage.page')} {Math.floor(pagination.offset / pagination.limit) + 1} {t('paymentNotificationsPage.of')} {Math.ceil(pagination.total / pagination.limit)}
                                </span>

                                <Button
                                    variant="outline-primary"
                                    onClick={handleNextPage}
                                    disabled={!pagination.hasMore}
                                >
                                    {t('paymentNotificationsPage.next')}
                                    <ChevronRight size={16} className="ms-1" />
                                </Button>
                            </Col>
                        </Row>
                    )}
                </>
            )}
        </Container>
    );
};

export default PaymentNotificationsPage;
